import { format, toDate, zonedTimeToUtc } from "date-fns-tz";
import { setHours, subBusinessDays, subHours } from "date-fns";
import { getTimezone } from "./dates";

import guestDemographicsFields from "../acf-json/group_66a3c3bc19804.json";


export const eventSortFn = (eventA, eventB) => {
	const aTs = eventA.start.getTime();
	const bTs = eventB.start.getTime();
	return aTs - bTs;
};

export const eventRevSortFn = (eventA, eventB) => {
	const aTs = eventA.start.getTime();
	const bTs = eventB.start.getTime();
	return bTs - aTs;
};

// eslint-disable-next-line max-statements
export const filteredCourses = (courses, filters) => courses.filter((course) => {

	if (filters.search) {
		const searchExp = filters.search.toLowerCase().replace(/ /ui, "|");
		if (`${course.title} ${course.description}`.toLowerCase().search(new RegExp(searchExp, "giu")) === -1) {
			return false;
		}
	}

	if (filters.courseType && course.type.indexOf(filters.courseType) === -1) {
		return false;
	}

	if (filters.facets) {
		for (const idx in filters.facets) {
			if (filters.facets[idx] && filters.facets[idx].length > 0) {
				if (!course[idx]) {
					return false;
				} else if (typeof course[idx] === "string" && course[idx] !== filters.facets[idx].trim()) {
					return false;
				} else if (
					typeof course[idx] === "object" && course[idx].indexOf(filters.facets[idx]) === -1) {
					return false;
				}
			}
		}
	}

	return true;
});

export const calculateFilters = (end = null, start = null) => {
	const date = new Date();
	const queryDateFormat = "yyyy-LL-dd";
	return {
		pager: {
			// eslint-disable-next-line camelcase
			per_page: 1000000
		},
		query: {
			// eslint-disable-next-line camelcase
			audience_type: [],
			diagnoses: [],
			facilitator: [],
			languages: [],
			location: [],
			programs: [],
			// eslint-disable-next-line camelcase
			start_date: {
				max: format(end || new Date(date.getFullYear(), date.getMonth() + 1, 0), queryDateFormat),
				min: format(start || new Date(date.getFullYear(), date.getMonth(), 1), queryDateFormat)
			}
		}
	};
};

export const sortEventsByDate = (eventsToSort) => {
	const sortedEvents = {};

	for (const idx in eventsToSort) {
		if (eventsToSort[idx].start) {
			try {
				const dateString = format(eventsToSort[idx].start, "yyyy-MM-dd");
				if (sortedEvents[dateString]) {
					sortedEvents[dateString].push(eventsToSort[idx]);
				} else {
					sortedEvents[dateString] = [eventsToSort[idx]];
				}
			// eslint-disable-next-line no-empty
			} catch (error) {}
		}
	}

	return sortedEvents;
};

export const getUTCTime = (timeString) => zonedTimeToUtc(timeString, getTimezone());

export const isCourseInFuture = (course) => zonedTimeToUtc(course.end, getTimezone()) > new Date();

export const isNotCourse = (course) => (newCourse) => newCourse.id !== course.id;

export const eventCancelled = (event) => Boolean(event.courseTiming?.eventCancelled);

export const isCourseType = (course) => course?.courseTypes?.edges[0]?.node?.slug || "in-person";

export const isInPersonEvent = (course) => course?.courseTypes?.edges[0].node.slug === "in-person";

export const isRemoteEvent = (course) => course?.courseTypes?.edges?.filter(({ node }) => node.slug === "online").length > 0 || false;

export const isHybridEvent = (course) => isInPersonEvent(course) && isRemoteEvent(course);

export const hasAvailableSeats = (course) => isHybridEvent(course)
	? course.availableSeats > 0 || course.availableOnlineSeats > 0
	: course.availableSeats > 0;

export const registrationIsOpen = (course) => isCourseInFuture(course) &&
	toDate(course.registrationClose, { timeZone: getTimezone() }) > new Date() &&
	(!course.registrationOpen || toDate(course.registrationOpen, { timeZone: getTimezone() }) < new Date()) &&
	!course.courseTiming?.noOnlineRegistration && !eventCancelled(course) && hasAvailableSeats(course);

export const generateRegistrationCloseTime = (course) => setHours(subBusinessDays(toDate(`${course.courseTiming.coursedate}T${course.courseTiming.starttime}`, { timeZone: getTimezone() }), 1), 15);

export const calendarViews = [
	["month", "Month", false],
	["week", "Week", false],
	["day", "Day", true],
	["agenda", "List", true]
];

export const getAllowedView = (view) => {
	if (calendarViews.filter(([viewSlug]) => viewSlug === view).length) {
		return view;
	}
	return "month";
};

export const urlLocationString = (course) => {
	const { location } = course;
	if (!location) {
		return null;
	}

	const { address } = location;
	const addressString = [
		location.name,
		address.addressline1,
		address.addressline2,
		address.city,
		address.state,
		address.zip
	].join(" ");

	return encodeURI(addressString);
};

export const googleMapsLink = (course) => `https://www.google.com/maps/search/?api=1&query=${urlLocationString(course)}`;

export const timeToShowZoomLink = (course) => {
	const timeZone = getTimezone();
	const now = new Date();
	const startTime = toDate(`${course.courseTiming.coursedate} ${course.courseTiming.starttime}`, { timeZone });
	const endTime = toDate(`${course.courseTiming.coursedate} ${course.courseTiming.endtime}`, { timeZone });
	return subHours(startTime, 1) < now && endTime > now;
};

export const shouldShowZoomLink = (course) => isRemoteEvent(course) && timeToShowZoomLink(course);

export const eventGuestsAllowed = (event) => (event?.coursesInfo?.guestsAllowed || event?.courseInfo?.guestsAllowed) &&
	event?.availableSeats && event?.availableSeats > 2;

/**
 * Guest types for the guest form
 */
export const guestTypes = [
	"Patient",
	"Caregiver",
	"Parent",
	"Family",
	"Professional"
];

/**
 * Fields for the guest form
 */
export const guestFields = () => {

	const genderOptions = guestDemographicsFields.fields.
		filter(({ key }) => key === "field_66a3c3bc2254f")[0].
		choices;

	return [
		{
			label: "First Name",
			name: "firstName",
			facilitator: true,
			attendee: true
		},
		{
			label: "Last Name",
			name: "lastName",
			facilitator: true,
			attendee: true
		},
		{
			label: "Guest Type",
			name: "type",
			type: "select",
			options: guestTypes,
			facilitator: true,
			attendee: true
		},
		{
			label: "Guest Email",
			name: "email",
			type: "email",
			facilitator: true,
			attendee: true
		},
		{
			label: "Guest Phone",
			name: "phone",
			type: "tel",
			facilitator: true,
			attendee: false
		},
		{
			label: "Guest Gender",
			name: "gender",
			type: "select",
			options: Object.keys(genderOptions),
			facilitator: true,
			attendee: true
		},
		{
			label: "Guest Notes",
			name: "notes",
			type: "textarea",
			facilitator: true
		},
		{
			label: "Is Guest a Minor?",
			name: "isMinor",
			type: "select",
			options: ["Yes", "No"],
			facilitator: true,
			attendee: true
		}
	];
};
